import { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';

interface TourStep {
  selector: string;
  title: string;
  description: string;
  noSpotlight?: boolean;
}

const TOUR_STEPS: TourStep[] = [
  {
    selector: '.menu-link[title="All Filings"]',
    title: 'All Filings',
    description: 'The page where you can upload, edit and download your XBRL files.',
  },
  {
    selector: '[data-tour="step-2"]',
    title: 'Balance',
    description: 'Credit will be deducted for each PDF upload.',
  },
  {
    selector: '[data-tour="step-3"]',
    title: 'Free Credits',
    description: 'You will be reimbursed free credits when your referee tops up.',
  },
  {
    selector: '[data-tour="step-4"]',
    title: 'Upload',
    description: 'Click here to upload your PDF file for XBRL conversion.',
  },
  {
    selector: '',
    title: 'Live Support',
    description: 'Use the chatbox at the bottom right for live customer support.',
    noSpotlight: true,
  },
];

const STORAGE_KEY = 'sg_onboarding_tour_done';
const PAD = 10;

export const SgOnboardingTour = () => {
  const [active, setActive] = useState(false);
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState<{ top: number; left: number; width: number; height: number } | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasScrolledRef = useRef(false);

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return;
    const timer = setTimeout(() => setActive(true), 1200);
    return () => clearTimeout(timer);
  }, []);

  const measure = useCallback(() => {
    const current = TOUR_STEPS[step];
    if (!current) return;

    if (current.noSpotlight) {
      setRect({ top: 0, left: 0, width: 0, height: 0 });
      return;
    }

    const el = document.querySelector(current.selector) as HTMLElement | null;
    if (!el) {
      setRect(null);
      return;
    }

    const r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) {
      setRect(null);
      return;
    }

    setRect({
      top: r.top - PAD,
      left: r.left - PAD,
      width: r.width + PAD * 2,
      height: r.height + PAD * 2,
    });
  }, [step]);

  useEffect(() => {
    if (!active) return;
    hasScrolledRef.current = false;

    const current = TOUR_STEPS[step];
    if (!current) return;

    if (!current.noSpotlight && !hasScrolledRef.current) {
      const el = document.querySelector(current.selector) as HTMLElement | null;
      if (el) {
        el.scrollIntoView({ behavior: 'auto', block: 'nearest' });
        hasScrolledRef.current = true;
      }
    }

    setTimeout(measure, 80);

    intervalRef.current = setInterval(measure, 400);

    const onLayout = () => measure();
    window.addEventListener('resize', onLayout);
    window.addEventListener('scroll', onLayout, true);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      window.removeEventListener('resize', onLayout);
      window.removeEventListener('scroll', onLayout, true);
    };
  }, [active, step, measure]);

  const dismiss = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, '1');
    setActive(false);
  }, []);

  const next = useCallback(() => {
    if (step < TOUR_STEPS.length - 1) {
      setStep(s => s + 1);
    } else {
      dismiss();
    }
  }, [step, dismiss]);

  const prev = useCallback(() => {
    if (step > 0) setStep(s => s - 1);
  }, [step]);

  if (!active || !rect) return null;

  const current = TOUR_STEPS[step];
  const isLast = step === TOUR_STEPS.length - 1;
  const isNoSpotlight = !!current.noSpotlight;

  const spaceBelow = window.innerHeight - (rect.top + rect.height);
  const tooltipBelow = isNoSpotlight ? false : spaceBelow > 230;
  const tooltipTop = tooltipBelow ? rect.top + rect.height + 14 : rect.top - 14;
  const tooltipLeft = isNoSpotlight
    ? window.innerWidth - 370
    : Math.min(Math.max(rect.left, 16), window.innerWidth - 360);

  const overlay = (
    <>
      {isNoSpotlight ? (
        /* Full dark overlay without spotlight hole */
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            zIndex: 99998,
            pointerEvents: 'none',
          }}
        />
      ) : (
        /* Dark overlay with spotlight hole via box-shadow */
        <div
          style={{
            position: 'fixed',
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
            borderRadius: 8,
            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.6)',
            zIndex: 99998,
            pointerEvents: 'none',
            transition: 'top 0.25s ease, left 0.25s ease, width 0.25s ease, height 0.25s ease',
          }}
        />
      )}

      {/* Clickable blocker */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ position: 'fixed', inset: 0, zIndex: 99997 }}
      />

      {/* Tooltip */}
      <div
        style={{
          position: 'fixed',
          bottom: isNoSpotlight ? 100 : (tooltipBelow ? undefined : window.innerHeight - tooltipTop),
          top: isNoSpotlight ? undefined : (tooltipBelow ? tooltipTop : undefined),
          left: tooltipLeft,
          width: 340,
          zIndex: 99999,
          transition: 'top 0.25s ease, bottom 0.25s ease, left 0.25s ease',
        }}
      >
        <div
          style={{
            background: '#fff',
            borderRadius: 12,
            padding: '20px',
            boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 500 }}>
              Step {step + 1} of {TOUR_STEPS.length}
            </span>
            <button
              onClick={dismiss}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 18, lineHeight: 1, padding: 0 }}
              aria-label="Close tour"
            >
              &times;
            </button>
          </div>

          <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700, color: '#111827' }}>
            {current.title}
          </h3>

          <p style={{ margin: '0 0 16px', fontSize: 13, color: '#4b5563', lineHeight: 1.5 }}>
            {current.description}
          </p>

          <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
            {TOUR_STEPS.map((_, i) => (
              <div
                key={i}
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: i === step ? '#2563eb' : '#d1d5db',
                  transition: 'background 0.2s',
                }}
              />
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              {step > 0 && (
                <button
                  onClick={prev}
                  style={{
                    background: 'none',
                    border: '1px solid #d1d5db',
                    borderRadius: 6,
                    padding: '6px 14px',
                    fontSize: 13,
                    color: '#374151',
                    cursor: 'pointer',
                    fontWeight: 500,
                  }}
                >
                  Back
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {!isLast && (
                <button
                  onClick={dismiss}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 13,
                    color: '#6b7280',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  Skip
                </button>
              )}
              <button
                onClick={next}
                style={{
                  background: '#2563eb',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '6px 18px',
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {isLast ? 'Done' : 'Next'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(overlay, document.body);
};
